import { useMemo, useState } from 'react';
import { nanoid } from 'nanoid';

import { ModalDispatchContext, ModalStateContext } from '../../contexts/ModalContext';

import type { ModalStateContextParams, ModalStateParams } from '../../contexts/ModalContext';

type ModalProviderReqParams = {
  children: React.ReactNode;
};

const ModalProvider = ({ children }: ModalProviderReqParams) => {
  const [openedModals, setOpenedModals] = useState<ModalStateContextParams>([]);

  const open = (targetModal: ModalStateParams) => {
    const { Component, props, uniqueId } = targetModal;
    setOpenedModals(modals => {
      return [
        ...modals,
        { Component, props: { ...props, open: true }, uniqueId: !uniqueId ? nanoid() : uniqueId },
      ];
    });
  };

  const close = (targetModal: Omit<ModalStateParams, 'props'>) => {
    const { uniqueId } = targetModal;

    setOpenedModals(modals => {
      const targetIndex = modals.findIndex(modal => modal.uniqueId === uniqueId);
      if (targetIndex < 0) {
        return modals;
      }

      return [
        ...modals.slice(0, targetIndex),
        { ...modals[targetIndex], props: { ...modals[targetIndex].props, open: false } },
        ...modals.slice(targetIndex + 1, modals.length),
      ];
    });
  };

  const closeAll = () => {
    setOpenedModals([]);
  };

  const exited = (targetModal: Omit<ModalStateParams, 'props'>) => {
    setOpenedModals(modals => {
      return modals.filter(modal => {
        return modal.uniqueId !== targetModal.uniqueId;
      });
    });
  };

  const dispatch = useMemo(() => ({ open, close, closeAll, exited }), []);

  return (
    <ModalStateContext.Provider value={openedModals}>
      <ModalDispatchContext.Provider value={dispatch}>{children}</ModalDispatchContext.Provider>
    </ModalStateContext.Provider>
  );
};

export default ModalProvider;
